import { $isListItemNode } from '@lexical/list';
import { $findMatchingParent } from '@lexical/utils';
import { Document, Packer, Paragraph, SectionType, TextRun } from 'docx';
import {
  $getSelection,
  $isElementNode,
  $isLineBreakNode,
  $isParagraphNode,
  type LexicalNode,
} from 'lexical';

import type { TextBlock } from '@/teoCore/TeoApi';

import type { TeoMarker } from '../EditorMarkerManager.svelte';
import { $isTeoTextNode, TeoTextNode } from './TeoTextNode';

export interface OffsetInNode {
  node: LexicalNode;
  offset: number;
}

export interface OffsetTeoMarker {
  start: OffsetInNode;
  end: OffsetInNode;
  marker: TeoMarker;
}

/**
 * Checks if the start of the current selection in the editor is within a node of the specified type.
 *
 * @param nodeType - The name of the type to check the node for.
 * @returns `true` if the current selection starts within a node of the specified type, otherwise `false`.
 */
export function $currentSelectionStartsInNodeType(nodeType: string): boolean {
  const selection = $getSelection();
  if (selection === null) return false;
  const startEnd = selection.getStartEndPoints();
  if (startEnd === null) return false;
  const startNode = startEnd[0].getNode();
  if (startNode.getType() === nodeType) return true;
  const parentMatchingNode = $findMatchingParent(
    startNode,
    (ancestor) => ancestor.getType() === nodeType,
  );
  return parentMatchingNode !== null;
}

/**
 * Creates a string representation of the content of a LexicalNode.
 * Tries to preserve the structure of paragraphs and line breaks with good readability.
 */
export function $getPlainText(node: LexicalNode): string {
  let textString = '';

  $traverseNodes(
    node,
    (node: LexicalNode) => {
      if ($isTeoTextNode(node)) {
        textString += node.getTextContent();
      } else if ($isListItemNode(node)) {
        textString += '- ';
      } else if ($isLineBreakNode(node)) {
        textString += '\n';
      } else if (!$isElementNode(node)) {
        textString += node.getTextContent();
      }
    },
    (node: LexicalNode) => {
      if ($isElementNode(node) && !node.isInline()) {
        if ($isParagraphNode(node)) {
          textString += '\n\n';
        } else {
          textString += '\n';
        }
      }
    },
  );
  return textString.trim();
}

/**
 * Creates a string representation of the content of a LexicalNode.
 * Keeps the character offsets consistent.
 */
export function $getTeoTextContent(node: LexicalNode): string {
  let textString: string = '';
  $traverseNodes(
    node,
    (node: LexicalNode) => {
      if (!$isElementNode(node)) {
        textString += node.getTextContent();
      }
    },
    (node: LexicalNode) => {
      if ($isElementNode(node) && !node.isInline()) {
        textString += '\n\n';
      }
    },
  );
  // Trim trailing paragraph and linebreak blocks from the end of the string
  return textString.replace(/\s+$/g, '');
}

export function $getTextArray(node: LexicalNode): TextBlock[] {
  const textArray: TextBlock[] = [];
  $traverseNodes(
    node,
    (node: LexicalNode) => {
      let newTextBlock: TextBlock | undefined = undefined;
      if ($isTeoTextNode(node)) {
        const string = node.getTextContent();
        if (string.length > 0) {
          newTextBlock = {
            string: string,
            type: 'text',
          };
        }
      } else if ($isListItemNode(node)) {
        newTextBlock = {
          string: '',
          type: 'text',
          length: 0,
          interpret_as: '\n- ',
        };
      } else if ($isLineBreakNode(node)) {
        newTextBlock = {
          string: '',
          type: 'linebreak',
          length: node.getTextContentSize(),
        };
      } else if (!$isElementNode(node)) {
        newTextBlock = {
          string: node.getTextContent(),
          type: 'text',
        };
        if (!['tab'].includes(node.getType())) {
          console.warn('Handling unknown node type in $getTextArray:', node.getType());
        }
      } else {
        if (!['root', 'paragraph'].includes(node.getType())) {
          console.warn('Ignoring unknown node type in $getTextArray:', node.getType());
        }
      }
      if (newTextBlock) {
        textArray.push(newTextBlock);
      }
    },
    // postCallback:
    (node: LexicalNode) => {
      if ($isElementNode(node) && !node.isInline()) {
        textArray.push({
          string: '\n\n',
          type: 'paragraph',
          length: 2,
        });
      }
    },
  );
  // Trim trailing paragraph and linebreak blocks from the end of the array
  while (
    textArray.length > 0 &&
    (textArray[textArray.length - 1]?.type === 'paragraph' ||
      textArray[textArray.length - 1]?.type === 'linebreak')
  ) {
    textArray.pop();
  }
  return textArray;
}

/**
 * Traverses a LexicalNode tree in pre-order (node, then children), with optional post-order callback.
 *
 * @param node - The root LexicalNode to start traversal from.
 * @param callback - Function called on each node before its children.
 *                   If it returns false, traversal of that branch is stopped.
 * @param postCallback - Optional function called on each node after its children (post-order).
 *
 * @returns false if traversal was stopped early, otherwise undefined.
 */
export function $traverseNodes(
  node: LexicalNode,
  callback: (node: LexicalNode) => boolean | void,
  postCallback?: (node: LexicalNode) => void,
) {
  const shouldContinue = callback(node);
  if (shouldContinue === false) return false;
  if ($isElementNode(node)) {
    node.getChildren().forEach((child: LexicalNode) => {
      $traverseNodes(child, callback, postCallback);
    });
  }
  postCallback?.(node);
}

export function $getLeafNodes(node: LexicalNode): LexicalNode[] {
  if (!$isElementNode(node)) {
    return [node];
  }
  const nodes: LexicalNode[] = [];
  node.getChildren().forEach((child: LexicalNode) => {
    nodes.push(...$getLeafNodes(child));
  });
  return nodes;
}

/**
 * Transforms a `TeoMarker` (which uses absolute offsets) into an `OffsetTeoMarker`
 * (which uses node-relative offsets) by traversing the given Lexical tree.
 *
 * This function walks the tree rooted at `rootNode`, mapping the absolute start and end
 * offsets of the marker to specific text nodes and their corresponding offsets.
 *
 * @param marker - The `TeoMarker` object containing absolute start and end offsets.
 * @param rootNode - The root `LexicalNode` of the tree to traverse.
 * @returns An `OffsetTeoMarker` with node-relative offsets if mapping is successful,
 *          or `null` if the marker could not be mapped (e.g., out of bounds).
 */
export function $transformMarkerToOffsetMarker(
  marker: TeoMarker,
  rootNode: LexicalNode,
): OffsetTeoMarker | null {
  let start: OffsetInNode | null = null;
  let end: OffsetInNode | null = null;
  let lastTextNode: TeoTextNode | null = null;
  let currentOffset = 0;

  // Traversal callback
  function visit(node: LexicalNode): boolean | void {
    let nextOffset = currentOffset;
    if (!$isElementNode(node)) {
      nextOffset += node.getTextContentSize();
    }

    if ($isTeoTextNode(node)) {
      if (!start && marker.start < nextOffset) {
        const offsetInNode = marker.start - currentOffset;
        start = {
          node,
          offset: Math.max(offsetInNode, 0),
        };
      }
      if (!end && marker.end < nextOffset) {
        const offsetInNode = marker.end - currentOffset;
        end = {
          node: offsetInNode > 0 || lastTextNode === null ? node : lastTextNode,
          offset: offsetInNode > 0 ? offsetInNode : (lastTextNode?.getTextContentSize() ?? 0),
        };
        if (start && end) return false; // Stop traversal
      }
      lastTextNode = node;
    }

    currentOffset = nextOffset;
  }

  // Post-order callback
  function postVisit(node: LexicalNode) {
    if (!node.isInline()) {
      currentOffset += 2;
    }
  }

  $traverseNodes(rootNode, visit, postVisit);

  // Normal case: both start and end found
  if (start && end) {
    return { start, end, marker };
  }

  // Edge case: marker ends at the very end of the text
  if (start && marker.end <= currentOffset && lastTextNode) {
    return {
      start,
      end: {
        node: lastTextNode,
        offset: (lastTextNode as TeoTextNode).getTextContentSize(),
      },
      marker,
    };
  }

  console.warn(
    'Invalid Marker:',
    marker,
    'Last offset:',
    currentOffset,
    'Last text node:',
    lastTextNode,
  );
  return null;
}

export function $docxExport(node: LexicalNode, filename: string): void {
  const paragraphs: Paragraph[] = [];
  let currentParagraph: Paragraph = new Paragraph({});
  // paragraphs.push(currentParagraph);
  let runningText = '';
  let currentFormatId: number | null = null;
  let currentFormat = {
    bold: false,
    italic: false,
    underline: false,
  };
  $traverseNodes(node, (node: LexicalNode) => {
    if ($isTeoTextNode(node)) {
      if (runningText !== '' && currentFormatId !== node.getFormat()) {
        currentParagraph.addChildElement(docxCreateTextRun(runningText, currentFormat));
        runningText = '';
        currentFormatId = node.getFormat();
        currentFormat = getFormatObj(node);
      } else if (currentFormatId === null) {
        currentFormatId = node.getFormat();
        currentFormat = getFormatObj(node);
      }
      runningText += node.getTextContent();
    } else if ($isParagraphNode(node)) {
      if (runningText !== '') {
        currentParagraph.addChildElement(docxCreateTextRun(runningText, currentFormat));
      }
      runningText = '';
      currentFormatId = null;
      currentParagraph = new Paragraph({});
      paragraphs.push(currentParagraph);
    } else if ($isListItemNode(node)) {
      if (runningText !== '') {
        currentParagraph.addChildElement(docxCreateTextRun(runningText, currentFormat));
      }
      runningText = '';
      currentFormatId = null;
      currentParagraph = new Paragraph({
        bullet: {
          level: 0,
        },
      });
      paragraphs.push(currentParagraph);
    }
  });
  if (runningText !== '') {
    currentParagraph.addChildElement(docxCreateTextRun(runningText, currentFormat));
  }
  const doc = new Document({
    sections: [
      {
        properties: {
          type: SectionType.CONTINUOUS,
        },
        children: paragraphs,
      },
    ],
  });
  Packer.toBlob(doc).then((blob) => downloadFile(blob, filename + '.docx'));
}

function getFormatObj(node: TeoTextNode): { bold: boolean; italic: boolean; underline: boolean } {
  return {
    bold: node.hasFormat('bold'),
    italic: node.hasFormat('italic'),
    underline: node.hasFormat('underline'),
  };
}

function docxCreateTextRun(
  text: string,
  format: { bold: boolean; italic: boolean; underline: boolean },
): TextRun {
  return new TextRun({
    text,
    bold: format.bold,
    italics: format.italic,
    underline: format.underline ? {} : undefined,
  });
}

function downloadFile(csvData: Blob | string, filename: string) {
  const url = window.URL.createObjectURL(csvData instanceof Blob ? csvData : new Blob([csvData]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();

  window.URL.revokeObjectURL(url);
  document.body.removeChild(link);
}

// function $checkLeafNodeForOffset(
//   searchNode: LexicalNode,
//   offset: number,
//   isStartOffset: boolean,
//   prevOffset: number = 0,
//   prevTextNode: TeoTextNode | null = null,
// ): OffsetInNode | null {
//   if ($isTeoTextNode(searchNode)) {
//     const textLength = searchNode.getTextContentSize();
//     const nextOffset = prevOffset + textLength;
//     if (offset < prevOffset && !isStartOffset && prevTextNode) {
//       return {
//         node: prevTextNode,
//         offset: prevTextNode.getTextContentSize(),
//       };
//     } else if (offset <= nextOffset) {
//       const offsetInNode = offset - prevOffset;
//       return {
//         node: searchNode,
//         offset: offsetInNode > 0 ? offsetInNode : 0,
//       };
//     }
//   }
//   return null;
// }

// export function $getSelectionOffsetsInNode(
//   searchNode: LexicalNode,
//   offset: number,
//   isStartOffset: boolean,
//   prevOffset: number = 0,
//   prevTextNode: TeoTextNode | null = null,
// ): OffsetInNode | TeoTextNode | null {
//   if (!$isElementNode(searchNode)) {
//     return checkLeafNodeForOffset(searchNode, offset, isStartOffset, prevOffset, prevTextNode);
//   }
//   const children = searchNode.getChildren();
//   let currentTextNode: TeoTextNode | null = prevTextNode;
//   let currentOffset = prevOffset;
//   for (const child of children) {
//     const searchResult = $getSelectionOffsetsInNode(
//       child,
//       offset,
//       isStartOffset,
//       currentOffset,
//       currentTextNode,
//     );
//     if (searchResult) {
//       if ($isTeoTextNode(searchResult)) {
//         currentTextNode = searchResult;
//       } else {
//         return searchResult;
//       }
//     }
//     currentOffset += child.getTextContentSize();
//   }
//   return null;
// }

// private getLeafNodes(node: LexicalNode): LexicalNode[] {
//     if (!$isElementNode(node)) {
//       return [node];
//     }
//     const nodes: LexicalNode[] = [];
//     node.getChildren().forEach((child: LexicalNode) => {
//       nodes.push(...this.getLeafNodes(child));
//     });
//     return nodes;
//   }

//   private getOffsetsInNodes(leafNodes: LexicalNode[], offsets: number[]): OffsetInNode[] {
//     const offsetsInNodes: OffsetInNode[] = [];
//     let currentOffset = 0;
//     leafNodes.forEach((node) => {
//       const textLength = node.getTextContentSize();
//       const nextOffset = currentOffset + textLength;
//       offsets.forEach((offset) => {
//         if (offset >= currentOffset && offset <= nextOffset) {
//           offsetsInNodes.push({
//             node,
//             offset: offset - currentOffset,
//           });
//         }
//       });
//       currentOffset = nextOffset;
//     });
//     return offsetsInNodes;
//   }

//   private transformMarkersToOffsetMarkers(markers: TeoMarker[], rootNode: LexicalNode): OffsetTeoMarker[] {
//     const leafNodes = this.getLeafNodes(rootNode);
//     const offsets = markers.flatMap((marker) => [marker.start, marker.end]);
//     const offsetsInNodes = this.getOffsetsInNodes(leafNodes, offsets);
//     const offsetMarkers: OffsetTeoMarker[] = [];
//     for (let i = 0; i < markers.length; i += 2) {
//       const start = offsetsInNodes[i]!;
//       const end = offsetsInNodes[i + 1]!;
//       offsetMarkers.push({
//         start,
//         end,
//         marker: markers[i]!,
//       });
//     }
//     return offsetMarkers;
//   }
// applyMarker(marker: TeoMarker) {
//   const { start, end } = marker;
//   this.editor.update(() => {
//     const root = $getRoot();
//     const selection = $createRangeSelection();
//     // selection.anchor.set(root.getKey(), start, 'text');
//     // selection.focus.set(root.getKey(), end, 'text');
//     selection.anchor.set(root.getKey(), start, 'element');
//     selection.focus.set(root.getKey(), end, 'element');
//     const affectedNodes = selection.extract();
//     affectedNodes.forEach((node) => {
//       if ($isTeoTextNode(node)) {
//         node.markers.push(marker);
//       }
//     });
//   }, {  });
// }
